/**
 * Admin « Boîte à outils » — recherche et filtres (pur, testé).
 *
 * Recherche sans accents ni casse dans le titre, la phrase, l'identifiant et
 * les familles ; filtre de niveau (l'outil sert à ce niveau, cf.
 * `toolFitsLevel`) ; filtre des liens (reliés à des SKILLCARDS ou non).
 */

import { SCHOOL_LEVELS } from '@jeraisonne/db';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { toolFitsLevel, type ToolDef, type ToolFamily } from './catalog';

export const FAMILY_LABEL: Record<ToolFamily, string> = {
  calcul: 'Calcul',
  fractions: 'Fractions',
  arithmetique: 'Arithmétique',
  proportionnalite: 'Proportionnalité',
  grandeurs: 'Grandeurs',
  geometrie: 'Géométrie',
  transformations: 'Transformations',
  espace: 'Espace',
  trigonometrie: 'Trigonométrie',
  algebre: 'Algèbre',
  fonctions: 'Fonctions',
  suites: 'Suites',
  integrales: 'Intégrales',
  probabilites: 'Probabilités',
  statistiques: 'Statistiques',
  vecteurs: 'Vecteurs',
  complexes: 'Complexes',
  matrices: 'Matrices',
  algorithmique: 'Algorithmique',
  chimie: 'Chimie',
  physique: 'Physique',
};

const RANK_LABEL: Record<number, string> = {
  1: '6e',
  2: '5e',
  3: '4e',
  4: '3e',
  5: '2nde',
  6: '1re',
  7: 'Tle',
  8: 'MPSI-PTSI',
  9: 'MP-PT',
};

/** « 4e → Tle », « Tle », « MPSI-PTSI → MP-PT » */
export function levelRangeText(tool: Pick<ToolDef, 'minRank' | 'maxRank'>): string {
  const lo = RANK_LABEL[tool.minRank] ?? String(tool.minRank);
  const hi = RANK_LABEL[tool.maxRank] ?? String(tool.maxRank);
  return lo === hi ? lo : `${lo} → ${hi}`;
}

/** niveaux (valeurs de SCHOOL_LEVELS) où l'outil sert */
export function levelsOf(tool: ToolDef): string[] {
  return SCHOOL_LEVELS.map((l) => l.value).filter((v) => toolFitsLevel(tool, v));
}

/** niveau proposé pour tester l'outil : celui du filtre s'il sert, sinon son premier niveau */
export function defaultTestLevel(tool: ToolDef, filter: string | null): string {
  if (filter && toolFitsLevel(tool, filter)) return filter;
  return levelsOf(tool)[0] ?? 'tle';
}

/** minuscules, sans accents ni ponctuation */
export function normSearch(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[’'\-/&]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export type LinkFilter = 'all' | 'linked' | 'unlinked';

export interface ToolFilter {
  query: string;
  /** valeur de SCHOOL_LEVELS, ou null pour tous */
  level: string | null;
  links: LinkFilter;
}

/** outils retenus, dans l'ordre du catalogue */
export function filterTools(
  tools: readonly ToolDef[],
  f: ToolFilter,
  linkCount: (id: string) => number,
): ToolDef[] {
  const words = normSearch(f.query).split(' ').filter(Boolean);
  return tools.filter((t) => {
    if (f.level && schoolLevelRank(f.level) !== null && !toolFitsLevel(t, f.level)) return false;
    const n = linkCount(t.id);
    if (f.links === 'linked' && n === 0) return false;
    if (f.links === 'unlinked' && n > 0) return false;
    if (words.length === 0) return true;
    const hay = normSearch(
      [t.title, t.pitch, t.id, ...t.families.map((x) => FAMILY_LABEL[x])].join(' '),
    );
    return words.every((w) => hay.includes(w));
  });
}
